import React from 'react';
import { motion } from 'framer-motion';
import { Trophy, Star, Flame } from 'lucide-react';

const PlayerCard = ({ player, rank, index }) => {
  const getRankColor = (rank) => {
    switch (rank) {
      case 1:
        return {
          gradient: 'from-yellow-400 via-yellow-500 to-yellow-600',
          glow: 'shadow-yellow-500/50',
          border: 'border-yellow-400/30'
        };
      case 2:
        return {
          gradient: 'from-gray-300 via-gray-400 to-gray-500',
          glow: 'shadow-gray-400/50',
          border: 'border-gray-300/30'
        };
      case 3:
        return {
          gradient: 'from-amber-600 via-amber-700 to-amber-800',
          glow: 'shadow-amber-600/50',
          border: 'border-amber-500/30'
        };
      default:
        return {
          gradient: 'from-blue-500 via-blue-600 to-blue-700',
          glow: 'shadow-blue-500/50',
          border: 'border-blue-400/30'
        };
    }
  };

  const colors = getRankColor(rank);

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.8, y: 20 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ delay: index * 0.1, type: "spring" }}
      whileHover={{ scale: 1.05, y: -5 }}
      className="relative group"
    >
      {/* Glow effect */}
      <div className={`absolute inset-0 bg-gradient-to-br ${colors.gradient} rounded-3xl blur-xl opacity-30 group-hover:opacity-50 transition-opacity duration-300`}></div>
      
      {/* Card */}
      <div className={`relative bg-white/90 backdrop-blur-xl rounded-3xl overflow-hidden border-2 ${colors.border} ${colors.glow} shadow-2xl`}>
        {/* Header with gradient */}
        <div className={`relative h-32 bg-gradient-to-br ${colors.gradient} overflow-hidden`}>
          {/* Animated particles */}
          {rank <= 3 && (
            <div className="absolute inset-0">
              {[...Array(8)].map((_, i) => (
                <motion.div
                  key={i}
                  className="absolute w-1 h-1 bg-white/60 rounded-full"
                  style={{
                    left: `${Math.random() * 100}%`,
                    top: `${Math.random() * 100}%`,
                  }}
                  animate={{
                    y: [0, -15, 0],
                    opacity: [0.3, 1, 0.3],
                  }}
                  transition={{
                    duration: 1.5 + Math.random(),
                    repeat: Infinity,
                    delay: Math.random() * 2,
                  }}
                />
              ))}
            </div>
          )}
          
          {/* Trophy icon */}
          <div className="absolute top-4 right-4">
            {rank === 1 && <Trophy className="w-8 h-8 text-white drop-shadow-lg animate-pulse-glow" />}
            {rank === 2 && <Trophy className="w-7 h-7 text-white drop-shadow-lg" />}
            {rank === 3 && <Trophy className="w-6 h-6 text-white drop-shadow-lg" />}
            {rank > 3 && <Star className="w-6 h-6 text-white" />}
          </div>
          
          {/* Pattern overlay */}
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAwIDEwIEwgNDAgMTAgTSAxMCAwIEwgMTAgNDAgTSAwIDIwIEwgNDAgMjAgTSAyMCAwIEwgMjAgNDAgTSAwIDMwIEwgNDAgMzAgTSAzMCAwIEwgMzAgNDAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS1vcGFjaXR5PSIwLjA1IiBzdHJva2Utd2lkdGg9IjEiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')] opacity-30"></div>
        </div>
        
        {/* Content */}
        <div className="relative pt-8 pb-6 px-6 -mt-12">
          {/* Rank badge */}
          <div className="flex justify-center mb-4">
            <div className={`relative w-24 h-24 bg-gradient-to-br ${colors.gradient} rounded-2xl flex items-center justify-center shadow-2xl ${colors.glow} border-4 border-white transform group-hover:rotate-6 transition-transform duration-300`}>
              <div className="text-4xl font-black text-white">
                {rank === 1 && "🥇"}
                {rank === 2 && "🥈"}
                {rank === 3 && "🥉"}
                {rank > 3 && `#${rank}`}
              </div>
            </div>
          </div>
          
          {/* Player info */}
          <div className="text-center space-y-2">
            <h3 className="text-xl font-bold text-gray-900 truncate">
              {player?.name}
            </h3>
            
            <p className="text-sm text-gray-600 font-medium">
              {player?.class}
            </p>
            
            {/* Points */}
            <div className="flex items-center justify-center gap-2 pt-4">
              <div className={`w-10 h-10 bg-gradient-to-br ${colors.gradient} rounded-xl flex items-center justify-center`}>
                <Flame className="w-5 h-5 text-white" />
              </div>
              <div className="text-left">
                <div className="text-2xl font-black text-gray-900">
                  {player?.totalPoints.toLocaleString()}
                </div>
                <div className="text-xs text-gray-500 font-medium">pontos</div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Bottom shine effect */}
        {rank <= 3 && (
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-white to-transparent opacity-50"></div>
        )}
      </div>
    </motion.div>
  );
};

export default PlayerCard;
